import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import {
  adminFetchCompanyInvoicingTypes,
  adminCreateCompanyInvoicingType,
  adminUpdateCompanyInvoicingType,
} from "../api/companyInvoicingType.api";
import {
  CreateCompanyInvoicingTypePayload,
  UpdateCompanyInvoicingTypePayload,
} from "../api/models/companyInvoicingType.models";
import { useTranslation } from "react-i18next";

const useAdminCompanyInvoicingTypes = ({ companyId, enabled }) => {
  const { t } = useTranslation();
  const [bearerToken, toast] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken, state.toastRef])
  );

  const queryClient = useQueryClient();

  const {
    data: invoicingTypes,
    error: invoicingTypesError,
    isLoading: isLoadingInvoicingTypes,
  } = useQuery({
    queryKey: ["companyInvoicingTypes", companyId],
    queryFn: () =>
      adminFetchCompanyInvoicingTypes(bearerToken as string, companyId),
    enabled: !!enabled,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: ["companyInvoicingTypes", companyId],
    });

  const createInvoicingTypeMutation = useMutation({
    mutationFn: (payload: CreateCompanyInvoicingTypePayload) =>
      adminCreateCompanyInvoicingType(bearerToken as string, payload),
    onError: (error) => {
      console.error("Failed to create company invoicing type:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("text-invoicing-type-create-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("text-invoicing-type-created"),
        life: 3000,
      });
      invalidate();
    },
  });

  const updateInvoicingTypeMutation = useMutation({
    mutationFn: (payload: UpdateCompanyInvoicingTypePayload) =>
      adminUpdateCompanyInvoicingType(bearerToken as string, payload),
    onError: (error) => {
      console.error("Failed to update company invoicing type:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("text-invoicing-type-update-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: "success",
        detail: t("text-invoicing-type-updated"),
        life: 3000,
      });
      invalidate();
    },
  });

  return {
    invoicingTypes,
    invoicingTypesError,
    isLoadingInvoicingTypes,
    createInvoicingType: createInvoicingTypeMutation.mutateAsync,
    updateInvoicingType: updateInvoicingTypeMutation.mutateAsync,
    isSavingInvoicingType:
      createInvoicingTypeMutation.isPending ||
      updateInvoicingTypeMutation.isPending,
  };
};

export default useAdminCompanyInvoicingTypes;
